/**
 * DraftPreviewHint
 *
 * When previewing an org in "View as org" (faithful) mode, the Draft status
 * filter is hidden for non-self-service orgs — which makes parent-built
 * drafts invisible and feels like data loss. Shows a hint when the previewed
 * org has drafts and the real user could switch to "Work as parent" to manage
 * them. Renders nothing otherwise.
 */

import React from 'react';
import { Alert } from 'antd';
import { useQuery } from '@apollo/react-hooks';
import COUNT_DRAFT_CAMPAIGN_ORDERS from '../../../../core/GraphQl/Queries/COUNT_DRAFT_CAMPAIGN_ORDERS';
import { PREVIEW_MODES } from '../../../../core/components/orgs/previewAccess';

const DraftPreviewHint = ({ currentUser }) => {
  const isEligibleParent = !!currentUser?.defaultRole?.permission?.isSelfService;
  // Undefined previewMode = faithful (pre-feature sessions)
  const isFaithfulPreview =
    !!currentUser?.isPreviewOrg && currentUser?.previewMode !== PREVIEW_MODES.WORK_AS_PARENT;
  const orgId = currentUser?.role?.org?.id;
  const enabled = isFaithfulPreview && isEligibleParent && !!orgId;

  const { data } = useQuery(COUNT_DRAFT_CAMPAIGN_ORDERS, {
    variables: {
      where: {
        status: 'DRAFT',
        orgs_some: { OR: [{ id: orgId }], NOT: [{ parentOrg_some: { id: orgId } }] },
      },
    },
    skip: !enabled,
  });

  if (!enabled) return null;
  const count = data?.campaignOrdersConnection?.aggregate?.count || 0;
  if (count === 0) return null;

  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={`This org has ${count} draft campaign${count > 1 ? 's' : ''}. You are viewing as the org — switch to "Work as parent" in the preview pill to manage drafts.`}
    />
  );
};

export default DraftPreviewHint;
